import "whatwg-fetch";
import { QuestionImageData } from "../Question/questionApi";
import { apiImage } from "../util/imageUtils";

const API_ORIGIN = process.env.REACT_APP_API_URL;
export const API_ROOT = API_ORIGIN + "api/v1/";
export const API_ROOT_V2 = API_ORIGIN + "api/v2/";

export type PageParams = {
  page: number;
  size: number;
  sortField?: string;
  sortOrder?: "ASC" | "DESC";
};

export function addParamsToUrl(urlString: string, params: Record<string, string | number | boolean | undefined>) {
  var queryString = Object.keys(params)
    .filter(key => params[key] !== undefined)
    .map(key => {
      return encodeURIComponent(key) + "=" + encodeURIComponent(params[key]!);
    })
    .join("&");

  return urlString + "?" + queryString;
}

function delay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

let _logoutCallback = () => { /* do nothing */ };

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
export function makeRequest(
  httpMethod: HttpMethod,
  url: string,
  body?: unknown,
  retryDelay = 0,
): Promise<Response> {
  let loginRequest = false;
  let requestOptions: RequestInit = {
    method: httpMethod,
    credentials: "include"
  };

  if (httpMethod === "POST" || httpMethod === "PUT" || httpMethod === "PATCH") {
    requestOptions.headers = { "Content-Type": "application/json" };
  }

  if (body && body instanceof FormData) {
    requestOptions.body = body;
    delete requestOptions.headers;
    if (body.has("password")) {
        loginRequest = true;
    }
  } else if (body) {
    requestOptions.body = JSON.stringify(body);
  }

  return fetch(url, requestOptions).then(response => {
    if (response.status === 401) {
      //_reAuthorizeCallback();
      // Needs to redirect such that login page is displayed again
      if (loginRequest) {
        return response;
      } else {
        _logoutCallback();
        throw new Error("Need to reauthorize");
      }
    } else if (response.status > 500 && (!retryDelay || retryDelay <= 1600)) {
      // Retry after 200ms, 400ms, 800ms, 1600ms. 5 tries in total.
      retryDelay = retryDelay * 2 || 200;
      console.log("Retrying request to " + url + " in " + retryDelay);
      return delay(retryDelay).then(function() {
        return makeRequest(httpMethod, url, body, retryDelay);
      });
    } else if (!response.ok) {
      // Try to get the error message if there is one
      return response.json()
        .then(body => body.message || "Unknown")
        .catch(() => "Unknown")
        .then(message => {
          const err = new Error(`${response.statusText} - ${message}`);
          Object.assign(err, { statusCode: response.status });
          throw err;
        });
    }

    return response;
  });
}

export type User = {
  firstName: string;
  lastName: string;
  username: string;
  uuid: string;
  enabled: boolean;
  subscriptions: Subscription[];
  lastUsedProductUuid: string;
};
export type Product = { name: string; uuid: string };

export type Subscription = {
  userUuid: string;
  product: Product;
  admin: boolean;
  questionWriter: boolean;
  questionAuditor: boolean;
  student: boolean;
  status: string;
};

let _accessToken: string | null = null;
let _reAuthorizeCallback: (() => void) | null = null;

let qBankApi = {
  doLogin: function(username: string, password: string) {
    const formData = new FormData();
    formData.append("username", username);
    formData.append("password", password);
    return makeRequest("POST", API_ORIGIN + "login", formData);
  },
  doLogout: function() {
    return fetch(API_ORIGIN + "logout", {
      method: "GET",
      credentials: "include",
      mode: "cors"
    });
  },
  sendPasswordReset: function(emailAddress: string) {
    return makeRequest("POST", API_ROOT + "initiatePasswordReset", {
      emailAddress: emailAddress
    });
  },
  completePasswordReset: function(token: string, emailAddress: string, password: string) {
    const completePasswordResetUrl = addParamsToUrl(
      API_ROOT + "completePasswordReset",
      { token: token }
    );
    return makeRequest("POST", completePasswordResetUrl, {
      emailAddress: emailAddress,
      newPassword: password
    });
  },
  getMe: function() {
    return makeRequest("GET", API_ROOT + "users/me").then(response =>
      response.json() as Promise<User>
    );
    //return fetch(API_ROOT + 'users/me', {
    //    method: 'GET',
    //    credentials: 'include',
    //    mode: 'cors'
    //});
  },
  changeActiveProduct: function (data: {productUuid: string, userUuid: string}) {
    return makeRequest("POST", API_ROOT + "lastused", data);
  },
  getProducts: function() {
    return makeRequest("GET", API_ROOT + "products").then(response =>
      response.json() as Promise<Product[]>
    );
  },
  getSubjects: function(productId: string) {
    return makeRequest("GET", API_ROOT + "subjects/products/" + productId).then(
      response => response.json()
    );
  },
  getSubject: function(subjectId: string) {
    return makeRequest("GET", API_ROOT + "/api/v1/subjects/" + subjectId).then(
      response => response.json()
    );
  },
  getCategories: function(subjectId: string) {
    return makeRequest(
      "GET",
      API_ROOT + "categories/subjects/" + subjectId
    ).then(response => response.json());
  },
  getCategory: function(categoryId: string) {
    return makeRequest("GET", API_ROOT + "categories/" + categoryId).then(
      response => response.json()
    );
  },
  getSubcategories: function(categoryId: string) {
    return makeRequest(
      "GET",
      API_ROOT + "subcategories/categories/" + categoryId
    ).then(response => response.json());
  },

  getQuestionImageThumbnails: function(questionId: string) {
      return makeRequest(
          "GET",
          API_ROOT + "questionImages/" + questionId + "/thumbnails"
      ).then(response => response.json());
  },
  deleteQuestionImage: function(questionId: string, imageId: string, imageType: string) {
      return makeRequest(
          "DELETE",
          API_ROOT + "questionImages/" + questionId + "/delete/" + imageId + "?imageType=" + imageType
      )
  },
  postQuestionImages: function(questionId: String, questionStemImages: QuestionImageData[], questionExplanationImages: QuestionImageData[]) {
      const formData = new FormData();
      questionStemImages.forEach(imageData => {
          formData.append("uploadingStemImages", imageData.image, imageData.fileName);
      });
      questionExplanationImages.forEach(imageData => {
          formData.append("uploadingExplanationImages", imageData.image, imageData.fileName);
      });

      return makeRequest("POST", API_ROOT + "questionImages/" + questionId, formData).then(
          response => response.json()
      );
  },
  getProductQuestions: function(productId: string, params: Record<string, string>) {
    var url = addParamsToUrl(
      API_ROOT + "questions/products/" + productId,
      params
    );
    return makeRequest("GET", url)
      .then(response => response.json())
      .then(({content}) => content);
  },
  getQuestionImages: function(questionId: string) {
    return makeRequest("GET", API_ROOT + "questionImages/" + questionId + "/images").then(
        response => response.json() as Promise<apiImage[]>
     );
  },
  getUser: function(userId: string) {
    return makeRequest("GET", API_ROOT + "users/" + userId).then(response =>
      response.json()
    );
  },
  createQuestionComment: function(questionId: string, comment: Record<string, string>) {
    return makeRequest(
      "POST",
      API_ROOT + "comments/questions/" + questionId,
      comment
    ).then(response => response.json());
  },
  getQuestionAllowedStatuses: function(questionId: string) {
    return makeRequest(
      "GET",
      API_ROOT + "questions/" + questionId + "/allowedStatuses",
    ).then(response => response.json());
  },
  setAccessToken: function(accessToken: string) {
    if (_accessToken !== accessToken) {
      _accessToken = accessToken;
      if (!accessToken && _reAuthorizeCallback) {
        _reAuthorizeCallback();
      }
    }
  },
  setReAuthorizeCallback: function(reAuthorizeCallback: () => void) {
    _reAuthorizeCallback = reAuthorizeCallback;
  },
  setLogoutCallback: function(logoutCallback: () => void) {
    _logoutCallback = logoutCallback;
  }
};

export default qBankApi;
